import { ReactNode, useEffect, useState } from 'react';
import Button from '../form-control/Button';
import { sentenceCase } from '../../utils/miscFunctions';
import { AlertTriangle, Info, CheckCircle, XCircle, X } from 'lucide-react';

export type ConfirmationType = 'info' | 'success' | 'warning' | 'danger';
interface IDeleteConfirmationProps {
  action: string;
  onClose: () => void;
  onConfirm: () => void;
  resourceName?: string;
  resourceType?: string;
  modalName?: string;
  confirmationMessage?: ReactNode;
  loading?: boolean;
  type?: ConfirmationType;
}

const ActionConfirmation = ({
  action,
  onClose,
  onConfirm,
  resourceName,
  resourceType,
  modalName,
  confirmationMessage,
  loading = false,
  type = 'warning' }: IDeleteConfirmationProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(loading)
  const iconConfig = {
    info: {
      icon: Info,
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-600',
      buttonColor: `${!isLoading ? 'bg-blue-600 hover:!bg-blue-700' : 'bg-gray-200'}`,
    },
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-100',
      textColor: 'text-green-600',
      buttonColor: `${!isLoading ? 'bg-green-600  hover:!bg-green-700' : 'bg-gray-200'}`,
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-600',
      buttonColor: `${!isLoading ? 'bg-yellow-600 hover:!bg-yellow-700' : 'bg-gray-200'}`,
    },
    danger: {
      icon: XCircle,
      bgColor: 'bg-red-100',
      textColor: 'text-red-600',
      buttonColor: `${!isLoading ? 'bg-red-600 hover:!bg-red-700' : 'bg-gray-200'}`,
    },
  };

  const { icon: Icon, bgColor, textColor, buttonColor } = iconConfig[type];


  return (
    <div className="flex flex-col items-center text-center w-full rounded-lg py-5 px-4 md:px-6">
      <div className="px-5 py-3 text-[22px] flex ">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 rounded-full bg-gray-100 p-1.5 text-gray-500  focus:outline-none  transition-colors duration-200 z-10"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className={`${bgColor} rounded-full p-3 mb-5 ring-4 ring-${bgColor}/30`}>
        <Icon className={`h-7 w-7 ${textColor}`} />
      </div>

      {confirmationMessage ? (
        <div className='w-full text-gray-600 mb-4 max-w-sm mx-auto font-light dark:text-darkBg-mainText'>
          {confirmationMessage}
        </div>
      ) : (
        <p className="w-full text-gray-600 mb-4 max-w-sm mx-auto font-light dark:text-darkBg-mainText" >
          Are you sure you want to {action}{' '}
          <b>{resourceType}</b> <b>{resourceName}</b>?
          <br />
          {type === 'danger' && (
            <span>This action cannot be undone.</span>
          )}
        </p>)}


      <div className="w-full flex gap-4 ">
        <Button
          label="Cancel"
          onClick={onClose}
          className="h-[50px] !w-1/2"
          bgColor='secondary'
        />

        <Button
          label={'Confirm'}
          onClick={() => {
            onConfirm();
            setIsLoading(true)
          }}
          curved
          disabled={isLoading}
          loading={isLoading}
          bgColor='primary'
          className={`h-[50px] ${buttonColor} px-10 !w-1/2`}
        />
      </div>
    </div>
  );
};

export default ActionConfirmation;

ActionConfirmation.displayName = 'ActionConfirmation';
